<template>
    <HeaderCardsLayout v-if="event" variant="No-Logo">
        <template #headline-extra>
            <BadgeDropDown
                :label="$t('status')"
                class="text-base font-normal"
                :current-value="getStatusBadgeCurrentValue()"
                :values="getStatusBadgeItems()"
                :status="getStatusBadgeStatus()"
                :tooltip="statusBadgeDisabledExplanation"
                size="Large-40"
                @change-value="(value: string) => handleStatusBadgeChange(value)"
            />
        </template>
        <template #headline>{{ event.nameShort }}</template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="event.nameLong">
                <BreadcrumbLink :target="{ name: 'external-events' }">
                    <AirplaneIcon class="mr-1 shrink-0" />
                    {{ $t('external-events') }}
                </BreadcrumbLink>
                <BreadcrumbLink :target="{ name: 'external-event-series', params: { seriesId: event.series.id } }">
                    {{ event.series.nameLong }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #buttons>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteEvent"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
        </template>
        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                :tab-panel-id="tabPanelId"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="tabPanelRef.focus()"
            />
        </template>
        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView />
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import { useBadgeDropDown } from '@/components/BadgeDropDown/useBadgeDropDown';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import AirplaneIcon from '@/components/Icon/AirplaneIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useTabNavigation } from '@/components/TabNavigation/useTabNavigation';
import { ExternalEventEventStatus, UpdateExternalEventEventStatusDocument } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { useExternalEventEvent } from '../composables/useExternalEventEvent';
import { externalEventEventStatusToBadgeVariant, getExternalEventEventStatusLabel } from '../externalEvent';
import { externalEventKey } from '../keys';

const tabPanelId = nanoid();
const props = defineProps<{ eventId: string }>();
const eventId = computed(() => props.eventId);

const tabPanelRef = ref();
const fluent = useFluent();

const { event, status, deleteDisabledExplanation, deleteEvent, onUpdateEvent, updateLocation, ...rest } =
    useExternalEventEvent(eventId);

provide(externalEventKey, {
    event,
    status,
    deleteDisabledExplanation,
    deleteEvent,
    onUpdateEvent,
    updateLocation,
    ...rest,
});

const statusOrder = ['fix', 'option', 'completed', 'discontinued'];

const {
    statusBadgeDisabledExplanation,
    getStatusBadgeCurrentValue,
    getStatusBadgeItems,
    getStatusBadgeStatus,
    handleStatusBadgeChange,
} = useBadgeDropDown(
    status,
    eventId,
    UpdateExternalEventEventStatusDocument,
    getExternalEventEventStatusLabel,
    externalEventEventStatusToBadgeVariant,
    Object.values(ExternalEventEventStatus).sort((x, y) => statusOrder.indexOf(x) - statusOrder.indexOf(y)),
);

const externalEventEventTabs = [
    { route: 'external-event-event-masterdata-tab', label: fluent.$t('external-event-event-master-data') },
    { route: 'external-event-event-allocations-tab', label: fluent.$t('external-event-event-allocations-tab') },
    { route: 'external-event-event-variants-tab', label: fluent.$t('external-event-event-variants-tab') },
];

const { tabNavigationItems, tabsDisabledText, activeTabHeadline } = useTabNavigation(externalEventEventTabs);

useHeadSafe({
    title: () => `${event.value?.nameShort ?? ''} - ${event.value?.series.nameShort ?? ''} - ${$t('external-events')}`,
});
</script>
